import React from "react"
import SectionCard from "../../../shared/components/SectionCard"
import EmptyStateBlock from "../../../shared/components/EmptyStateBlock"
import StatusPill from "../../../shared/components/StatusPill"
import { ui } from "../../../shared/styles/uiSystem"

function getLocalDateKey(value) {
  if (!value) return ""

  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ""

  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, "0")
  const dd = String(date.getDate()).padStart(2, "0")

  return `${yyyy}-${mm}-${dd}`
}

export default function PatientAppointmentsTab({
  patientAppointments,
  formatDateOnly,
  formatTimeOnly,
  navigate,
  resolveSafeClinicId
}) {
  const rows = Array.isArray(patientAppointments) ? patientAppointments : []

  return (
    <SectionCard title="Citas" compact>
      {!rows.length ? (
        <EmptyStateBlock title="Sin citas registradas" compact />
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <StatusPill tone="neutral">Total: {rows.length}</StatusPill>
          </div>

          {rows.map((item, index) => (
            <AppointmentCard
              key={item.id || index}
              item={item}
              formatDateOnly={formatDateOnly}
              formatTimeOnly={formatTimeOnly}
              onOpen={() => {
                const currentClinicId = resolveSafeClinicId()
                const targetClinicId =
                  item.clinicId && item.clinicId !== "ALL"
                    ? String(item.clinicId)
                    : currentClinicId

                if (!targetClinicId) {
                  alert("No hay una clínica válida seleccionada.")
                  return
                }

                const appointmentStart =
                  item.start ||
                  item.startTime ||
                  item.scheduledAt ||
                  item.appointmentDate ||
                  ""

                localStorage.setItem(
                  "odontoflow_open_appointment",
                  JSON.stringify({
                    appointmentId: item.id,
                    clinicId: targetClinicId,
                    date: getLocalDateKey(appointmentStart) || null
                  })
                )

                navigate("/agenda")
              }}
            />
          ))}
        </div>
      )}
    </SectionCard>
  )
}

function AppointmentCard({ item, formatDateOnly, formatTimeOnly, onOpen }) {
  const treatmentLabel = item.treatmentName || item.reason || "Sin tratamiento definido"
  const doctorLabel = item.doctorName || "Sin doctor"
  const sourceLabel = translateAppointmentSource(item.source)

  return (
    <div
      style={{
        borderRadius: 16,
        border: "1px solid #e2e8f0",
        background: "#ffffff",
        padding: 12,
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        alignItems: "center",
        flexWrap: "wrap"
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          minWidth: 0,
          flex: "1 1 420px"
        }}
      >
        <div
          style={{
            minWidth: 78,
            borderRadius: 14,
            border: "1px solid #dbeafe",
            background: "#eff6ff",
            padding: "9px 10px",
            textAlign: "center"
          }}
        >
          <div
            style={{
              fontSize: 16,
              fontWeight: 900,
              color: "#0f172a",
              lineHeight: 1
            }}
          >
            {formatTimeOnly(item.start)}
          </div>
          <div style={{ fontSize: 11.5, color: "#475569", fontWeight: 800, marginTop: 4 }}>
            {formatDateOnly(item.start)}
          </div>
        </div>

        <div style={{ display: "grid", gap: 6, minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 900,
              color: "#0f172a",
              lineHeight: 1.2,
              wordBreak: "break-word"
            }}
          >
            {treatmentLabel}
          </div>

          <div style={{ fontSize: 12.5, color: "#475569", lineHeight: 1.35 }}>
            {doctorLabel}
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <StatusPill tone={appointmentStatusTone(item.status)}>
              {translateAppointmentStatus(item.status)}
            </StatusPill>

            {sourceLabel ? <StatusPill tone="neutral">{sourceLabel}</StatusPill> : null}
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={onOpen}
        style={{
          ...ui.secondaryButton,
          minHeight: 34,
          background: "#eff6ff",
          border: "1px solid #bfdbfe",
          color: "#1d4ed8"
        }}
      >
        Abrir en agenda
      </button>
    </div>
  )
}

function appointmentStatusTone(status) {
  const raw = String(status || "").trim().toUpperCase()
  if (["CONFIRMED", "CHECKED_IN", "COMPLETED", "DONE"].includes(raw)) return "success"
  if (["SCHEDULED", "PROGRAMADA", "BOOKED", "PROGRAMMED"].includes(raw)) return "info"
  if (["PENDING", "PENDIENTE"].includes(raw)) return "warning"
  if (["CANCELLED", "CANCELADA", "NO_SHOW"].includes(raw)) return "danger"
  return "neutral"
}

function translateAppointmentStatus(status) {
  const raw = String(status || "").trim().toUpperCase()

  if (raw === "CONFIRMED") return "Confirmada"
  if (raw === "CHECKED_IN") return "En atención"
  if (raw === "SCHEDULED" || raw === "BOOKED" || raw === "PROGRAMMED") return "Programada"
  if (raw === "PENDING") return "Pendiente"
  if (raw === "CANCELLED") return "Cancelada"
  if (raw === "DONE" || raw === "COMPLETED") return "Realizada"
  if (raw === "NO_SHOW") return "No asistió"
  if (raw === "HELD") return "Apartada"

  if (!raw) return "Sin estado"
  return raw
}

function translateAppointmentSource(source) {
  const raw = String(source || "").trim().toUpperCase()

  if (raw === "MANUAL") return "Manual"
  if (raw === "PLAN" || raw === "PATIENTS_PLAN_TAB") return "Plan"
  if (raw === "ODONTOGRAM") return "Odontograma"
  if (raw === "AI") return "IA"
  if (raw === "ONLINE") return "En línea"
  if (!raw) return "Manual"

  return raw
}
